import React, { useState } from "react";
import { Table, Tag, Button, Tooltip, Input } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { DownloadOutlined, FilePdfOutlined } from "@ant-design/icons";
import { CV_ANALYZER_API_BASE_URL } from "../../constants/config"
import Papa from "papaparse";
import "./TableLayout.css"; // Import custom CSS file for styling


// Truncate long text for tooltips
const renderMultilineTooltip = (text, maxLength = 150) => {
  if (!text) return null;

  const truncatedText = text.length > maxLength ? text.substring(0, maxLength) + "..." : text;

  return (
    <Tooltip title={text}>
      <span className="tooltip-text">{truncatedText}</span>
    </Tooltip>
  );
};


const ResumeResultsTable = ({ data }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState("");

  const tableData = data.map((item, index) => ({
    key: String(index + 1),
    name: item["Candidate Name"],
    experienceYearsScore: item["Years of Experience score (15)"],
    experienceYearsAnalysis: item["Years of Experience Analysis"],
    relevantExperienceScore: item["Relevant Experience score (25)"],
    irrelevantExperiencePenalty: item["Irrelevant Experience penalty"],
    experienceRelevanceAnalysis: item["Experience Relevance Analysis"],
    skillsScore: item["Skills score (15)"],
    skillsAnalysis: item["Skills Analysis"],
    projectsScore: item["Projects score (20)"],
    projectsAnalysis: item["Projects Analysis"],
    educationScore: item["Education score (15)"],
    educationAnalysis: item["Education Analysis"],
    certificationsScore: item["Certification score (10)"],
    certificationsAnalysis: item["Certifications Analysis"],
    acceptanceScore: item["Acceptance score (100)"],
    status: item["Status"],
    viewCV: item["viewCV"],
  }));

  const filteredData = tableData.filter((item) =>
    Object.values(item).some((value) =>
      value && value.toString().toLowerCase().includes(searchText.toLowerCase())
    )
  );

  const downloadCSV = () => {
    try {
      const fields = [
        "name",
        "experienceYearsScore",
        "experienceYearsAnalysis",
        "relevantExperienceScore",
        "irrelevantExperiencePenalty",
        "experienceRelevanceAnalysis",
        "skillsScore",
        "skillsAnalysis",
        "projectsScore",
        "projectsAnalysis",
        "educationScore",
        "educationAnalysis",
        "certificationsScore",
        "certificationsAnalysis",
        "acceptanceScore",
        "status",
      ];

      // Filter and re-map only required fields
      const filteredDataForCSV = filteredData.map((row) => {
        const filteredRow = {};
        fields.forEach((field) => {
          filteredRow[field] = row[field];
        });
        return filteredRow;
      });

      const csv = Papa.unparse(filteredDataForCSV);

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "resume_analysis_results.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error("Error generating CSV:", error);
    }
  };

  const columns = [
    { 
      title: "Candidate Name", 
      dataIndex: "name", 
      key: "name", 
      width: 150
    },
    { 
      title: "Years of Experience Score (15)", 
      dataIndex: "experienceYearsScore", 
      key: "experienceYearsScore",
      sorter: (a, b) => a.experienceYearsScore - b.experienceYearsScore
    },
    {
      title: "Years of Experience Analysis",
      dataIndex: "experienceYearsAnalysis",
      key: "experienceYearsAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    { 
      title: "Relevant Experience Score (25)",
       dataIndex: "relevantExperienceScore", 
       key: "relevantExperienceScore",
       sorter: (a, b) => a.relevantExperienceScore - b.relevantExperienceScore 
    },
    {
      title: "Irrelevant Experience Penalty",
      dataIndex: "irrelevantExperiencePenalty",
      key: "irrelevantExperiencePenalty",
    },
    {
      title: "Experience Relevance Analysis",
      dataIndex: "experienceRelevanceAnalysis",
      key: "experienceRelevanceAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    { 
      title: "Skills Score (15)", 
      dataIndex: "skillsScore", 
      key: "skillsScore" ,
      sorter: (a, b) => a.skillsScore - b.skillsScore  
    },
    {
      title: "Skills Analysis",
      dataIndex: "skillsAnalysis",
      key: "skillsAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    { 
      title: "Projects Score (20)", 
      dataIndex: "projectsScore", 
      key: "projectsScore" ,
      sorter: (a, b) => a.projectsScore - b.projectsScore 
    },
    {
      title: "Projects Analysis",
      dataIndex: "projectsAnalysis",
      key: "projectsAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    { 
      title: "Education Score (15)", 
      dataIndex: "educationScore", 
      key: "educationScore" ,
      sorter: (a, b) => a.educationScore - b.educationScore 
    },
    {
      title: "Education Analysis",
      dataIndex: "educationAnalysis",
      key: "educationAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    { 
      title: "Certifications Score (10)", 
      dataIndex: "certificationsScore", 
      key: "certificationsScore" ,
      sorter: (a, b) => a.certificationsScore - b.certificationsScore 
    },
    {
      title: "Certifications Analysis",
      dataIndex: "certificationsAnalysis",
      key: "certificationsAnalysis",
      render: (text) => renderMultilineTooltip(text),
    },
    {
      title: "Acceptance Score (100)",
      dataIndex: "acceptanceScore",
      key: "acceptanceScore",
      sorter: (a, b) => a.acceptanceScore - b.acceptanceScore,
      defaultSortOrder: "descend",
      render: (score) => <span>{score}</span>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      fixed: "right",
      filters: [
        { text: "Accepted", value: "Accepted" },
        { text: "Rejected", value: "Rejected" },
      ],
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <Tag color={status === "Accepted" ? "green" : "red"} className="status-tag">
          {status}
        </Tag>
      ),
    },
    {
      title: "View CV",
      dataIndex: "viewCV",
      key: "viewCV",
      render: (viewCV) =>
        viewCV && viewCV.url ? (
          <Tooltip title={viewCV.file_name}>
            <a href={`${CV_ANALYZER_API_BASE_URL}${viewCV.url}`} target="_blank" rel="noopener noreferrer">
              <FilePdfOutlined style={{ fontSize: "20px", color: "#1890ff" }} />
            </a>
          </Tooltip>
        ) : (
          <span className="no-cv">No CV</span>
        ),
    },
  ];

  return (
    <div className="resume-results-container">
      <div className="header">
        <h2 className="title">Analysis Results</h2>

        <Input.Search
          placeholder="Search in all columns"
          allowClear
          enterButton={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}  // for typing
          onSearch={(value) => setSearchText(value)}       // for clicking Search
          style={{
            width: 300,
            borderRadius: 20,
            marginBottom: 20,
          }}
        />

        <Button type="primary" icon={<DownloadOutlined />} onClick={downloadCSV} style ={{ marginBottom: 20}}>
          Download CSV
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={tableData.filter((row) =>
          Object.values(row).some(
            (value) =>
              value &&
              value.toString().toLowerCase().includes(searchText.toLowerCase())
          )
        )}
        pagination={{
          pageSize: 10,
          current: currentPage,
          onChange: (page) => setCurrentPage(page),
          total: tableData.length,
        }}
        scroll={{ x: "max-content" }}
      />
    </div>
  );
};

export default ResumeResultsTable;
